import React from "react";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parseISO, isValid } from "date-fns";

const CustomDatePicker = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  sx,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Convert incoming "YYYY-MM-DD" string to a Date object for the picker
  const dateValue = value ? parseISO(value) : null;

  // Shared input styling matching the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& .MuiInputAdornment-root .MuiIconButton-root": {
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      marginRight: "1px",
      "&:active": {
        transform: "scale(0.85)",
      },
    },
  };

  // Popper styling for the interactive calendar dropdown
  const popperStyleProps = {
    "& .MuiPaper-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(
              colors.primary[400].slice(3, 5),
              16,
            )}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.95)`
          : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(16px)",
      border: `1px solid ${
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.15)"
          : "rgba(0, 0, 0, 0.1)"
      }`,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
          : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
      borderRadius: "12px",
      mt: 1,
    },
    "& .MuiPickersDay-root": {
      borderRadius: "8px",
      transition: "all 0.2s",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
      "&.Mui-selected": {
        backgroundColor: colors.blueAccent[600],
        color: "#ffffff",
        fontWeight: "bold",
        "&:hover": {
          backgroundColor: colors.blueAccent[500],
        },
      },
      "&.MuiPickersDay-today": {
        borderColor: colors.blueAccent[500],
      },
    },
    "& .MuiPickersYear-yearButton": {
      borderRadius: "8px",
      "&.Mui-selected": {
        backgroundColor: colors.blueAccent[600],
        color: "#ffffff",
        fontWeight: "bold",
      },
    },
    "& .MuiPickersMonth-monthButton": {
      borderRadius: "8px",
      "&.Mui-selected": {
        backgroundColor: colors.blueAccent[600],
        color: "#ffffff",
        fontWeight: "bold",
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={label}
        value={dateValue}
        onChange={(newValue) => {
          if (newValue && isValid(newValue)) {
            // Output standard format for backend consistency
            onChange(format(newValue, "yyyy-MM-dd"));
          } else {
            onChange(null);
          }
        }}
        slotProps={{
          textField: {
            variant: "filled",
            name: name,
            onBlur: onBlur,
            error: error,
            helperText: helperText,
            fullWidth: true,
            sx: { ...inputStyleProps, ...sx },
            InputProps: {
              disableUnderline: true,
              style: { borderRadius: "8px", fontSize: "0.95rem" },
            },
            InputLabelProps: {
              style: { fontSize: "0.95rem" },
            },
          },
          popper: {
            sx: popperStyleProps,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
